import makeRequest from "../networkClient"
import { _formatDate, _get, _post, _put } from "./util"
import { urlForResource } from "../common/constants"
import Rmbx from "../util"
import { getFlagEnabled } from "../getFlagValue"

export const switchCompanyApi = employeeId => _post(`/api/v4/user_employees/${employeeId}/switch_company/`)

// Company EoD question library (WA-9516).
export const createEodQuestion = data => _post("/api/v4/eod_questions/", data, 201)
export const createEodQuestionImagePutUrl = data => _post("/api/v4/eod_questions/create_put_url/", data, 200)
// Sets is_active for one or more questions (default or custom) in a single request -- the
// only mutation available for a default question, since those can't be edited or retired.
export const bulkUpdateEodQuestionsIsActive = (ids, active) => _post("/api/v4/eod_questions/bulk_update_is_active/", { ids, active }, 200)
// POST (not GET) since callers pass every employee_id currently in view, which can run into the
// thousands for large companies -- too many to safely comma-join into a query string.
export const getEodAnswers = (employeeIds, startDate, endDate) => _post("/api/v4/eod_questions/answers_by_employee/", { employee_ids: employeeIds, start_date: startDate, end_date: endDate }, 200)

export const addWorkerToShift = (workShift, employeeId) =>
    _post(
        "/api/v3/employee_work_shifts/",
        {
            work_shift: workShift.id,
            employee: employeeId,
            state: "CLOSED",
        },
        201
    )

export const bulkCreateRows = (resourceName, data, expectedStatusCode = 200) => _post(`${urlForResource[resourceName]}bulk_insert/`, data, expectedStatusCode)

export const bulkUpdateQuantities = qtyList => _post("/api/v3/quantities/bulk_update/", { quantities: JSON.stringify(qtyList) }, 200)

export const changeCostCode = (workshiftId, data) => _post(`/api/v3/work_shifts/${workshiftId}/change_cost_code/`, data)

export const changePassword = data => _post("/api/v3/users/change_password/", data)

export const createAbsence = data => _post("/api/v3/absences/", data, 201)

export const createApiTokenIntegrationName = (integrationName, coId, empId) =>
    _post("/api/v3/company_integration/", {
        company_integration_name: integrationName,
        company: coId,
        created_by: empId,
    })

export const createAutodeskToken = code => _post(urlForResource.autodeskToken, { code }, 201)

export const createCompanyFormStore = (body, fields = []) => _post("/api/v4/company_form_stores/", body, 201, false, false, false, { fields })

export const generateS3Urls = (body, resource) => {
    const resourceUrl = resource ? urlForResource?.[resource] : null
    if (resourceUrl) {
        return _post(`${resourceUrl}bulk_reference/`, body)
    }
    return _post("/api/v4/company_form_stores/bulk_reference/", body)
}

/**
 * Generate a signed S3 PUT URL and proxy URL for worker profile support documentation
 * @param {{ content_type: string, extension: string, resource: string }} payload -
 *      The request payload, including the content type, file extension,
 *      and resource type (certifications, licenses, unions, or a future resource)
 * @return {Promise} - A promise that resolves to the response containing the signed S3 PUT URL,
 *      proxy URL, relative url, and uuid
 */
export const generateResourcePutURL = payload => _post("/api/v4/company/generate_resource_put_url/", payload, 200)

// _post will append a trailing slash to "create_dependencies=True", but
// that's OK: create_dependencies can be any truthy value
export const createQuantity = (date, cc, value) =>
    _post(
        "/api/v3/quantities/?create_dependencies=True",
        {
            client_created: date,
            project_cost_code: "",
            cost_code: cc["id"],
            project: cc["project"],
            value: value,
        },
        201
    )

export const sendReferralEmails = data => _post("/api/v4/tm-invite/", data, 204)

export const createRow = (resourceName, data) => _post(urlForResource[resourceName], data, 201)

export const createWorkShiftV4 = params => _post("/api/v4/work_shifts/", params, 201)

export const upsertTimecard = (data, forceNewTimecard) => {
    return _post(`/api/v4/work_shifts/upsert_timecard/?force_new_timecard=${forceNewTimecard}`, data, 200, false, false, true)
}

export const pasteCell = data => {
    return _post("/api/v4/work_shifts/paste_cell/", data, 200, false, false, true)
}

export const createWorkComponent = workComponent => _post("/api/v3/work_components/", workComponent, 201)

export const createWorkshift = workshift => _post("/api/v3/work_shifts/", workshift, 201)

export const deactivateIntegrationToken = id => _post(`/api/v3/company_integration/${id}/deactivate/`)

export const deleteItemsById = (resourceName, objectIds, projectId = null) =>
    _post(
        `${urlForResource[resourceName]}bulk_delete/`,
        {
            ids: objectIds,
            project_id: projectId,
        },
        204
    )

export const undeleteItemsById = (resourceName, objectIds) => _post(`${urlForResource[resourceName]}bulk_undelete/`, { ids: objectIds }, 204)

export const duplicateWorkshift = (workshiftId, newDate) => _post(`/api/v3/work_shifts/${workshiftId}/duplicate/`, { new_date: _formatDate(newDate) }, 201)

export const autodeskLogin = data => _post("/auth/autodesk-login/", data)

// Autodesk company/hub self-provisioning (WA-9395).
export const createAutodeskCompany = data => _post("/api/v4/autodesk/companies/", data, 201)

export const linkAutodeskCompany = data => _post("/api/v4/autodesk/companies/link/", data)

export const login = data => _post("/api/v3/users/auth_token/", data)

export const loginCheck = data => _post("/api/v4/employees/login_check/", data)

export const guestLogin = data => _post("/api/v4/guest_login/", data)

export const sendFormData = (form_data, resource, method = "POST", apiVersion = 4, headers = null, extraParams = null) => {
    const params = {
        ...{
            method: method,
            body: form_data,
        },
        ...extraParams,
    }
    if (headers) {
        params["headers"] = headers
    }
    return fetch(`/api/v${apiVersion}/${resource}/`, params)
}

/**
 * Extract form fields from an uploaded image or PDF using AI.
 *
 * @param {File} file - The image or PDF file to analyze
 * @param {string} [context] - Optional additional context to guide extraction
 *   (e.g., customer requirements, field naming preferences, call transcripts)
 * @returns {Promise<{fields: Array, error?: string}>} Extracted fields in AIExtractedField format
 */
export const extractFieldsFromForm = async (file, context = "") => {
    const formData = new FormData()
    formData.append("file", file)
    if (context) {
        formData.append("context", context)
    }

    // Use sendFormData with auth headers
    const { getAuthToken, getAuthType } = await import("../util")
    const authToken = getAuthToken()
    const authType = getAuthType() || "Token"
    const headers = authToken ? { Authorization: `${authType} ${authToken}` } : {}

    const response = await sendFormData(formData, "schema-builder/extract-fields", "POST", 4, headers)

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to extract fields: ${response.status}`)
    }

    return response.json()
}

export const sendForemanInvite = (companyId, data) => _post(`/api/v3/companies/${companyId}/company_employees/invite/`, data)

export const sendMassText = (companyId, message, employeeIds) =>
    _post(`/api/v3/companies/${companyId}/company_employees/masstext/`, {
        message: message,
        employee_ids: employeeIds,
    })

export const updateCompanySignatureSettings = signatureSettings => _post("/api/v4/company_options/", signatureSettings, 201)

/** Upsert the company's re-signature field classification overrides, returning the resolved map. */
export const updateSignatureFieldClassifications = classifications => _post("/api/v4/signature_field_classifications/", classifications, 200)

export const updateItemsValueById = (resourceName, objectIds, field, value, updateUsingResponseData = false) =>
    _post(
        `${urlForResource[resourceName]}bulk_update/`,
        {
            ids: objectIds,
            field: field,
            value: value,
        },
        // updateUsingResponseData should be set to true
        // if we're returning response data from the bulk_update
        // backend endpoint for this resource (status 200)
        // rather than no data (status 204)
        updateUsingResponseData ? 200 : 204
    )

export const updateLockedPeriod = date => _post("/api/v4/company_options/", { lock_end_date: date }, 201)

export const updateGroupSchemaSettings = (schemaId, groupSchemaSettings) => _post(`/api/v4/company_group_schemas/${schemaId}/update/`, groupSchemaSettings, 200)

export const updateSchemaProjectAssignments = (schemaId, projectAssignments) => _put(`/api/v4/custom_form_schemas/${schemaId}/projects/`, projectAssignments, 200)

export const getPicklistGroupsAndProjects = (itemType, itemIds) =>
    _post("/api/v4/group_project_modifiers/group_project_tree/", {
        item_type: itemType,
        item_ids: itemIds,
    })

export const updatePicklistGroupsAndProjects = (itemType, itemIds, groupSettings, projectAssignments) =>
    _post("/api/v4/group_project_modifiers/bulk_update/", {
        item_type: itemType,
        item_ids: itemIds,
        groups: groupSettings,
        projects: projectAssignments,
    })

export const workflowActionById = (resourceName, objectIds, action, queryParams = {}, additionalData = {}) => {
    // adding ignore_schema_types allows us to update bundle stores, which don't come back in the default queryset
    if (resourceName === "companyFormStores") queryParams = { ...queryParams, ignore_schema_types: true }
    const queryString = Rmbx.util.serializeQueryParams(queryParams)

    // _post appends a trailing slash to the query string
    return makeRequest(
        {
            method: "POST",
            url: `${urlForResource[resourceName]}bulk_workflow_action/?${queryString}`,
            body: JSON.stringify({
                ids: objectIds,
                action: action,
                ...additionalData,
            }),
        },
        200
    )
}

export const createDailySummaryPreference = dailySummaryPreference => _post(urlForResource["dailySummaryPreferences"], dailySummaryPreference, 201)

export const transformDataAPI = (transform, only_from_selected_project = false) => {
    return _post(
        `/api/v4/company_form_stores/transform/?project_id=${transform.project_id}`,
        {
            transform_id: transform.transform_id,
            store_ids: transform.store_ids,
            project_id: transform.project_id,
            ...(getFlagEnabled("WA-7858-web-tk-transform-project-limited") && only_from_selected_project ? { only_from_selected_project: true } : {}),
            ...(transform?.variant_name ? { variant_name: transform?.variant_name } : {}),
        },
        200,
        false,
        false,
        true
    )
}

export const submitSelectedFormShareInfo = data => {
    return _post("/api/v4/guest_form_shares/share_form/", data, 200)
}

export const removePreviousCollaborator = guest_id => {
    return _post(`/api/v4/guest_users/${guest_id}/delete/`, null, 200)
}

export const updatePermissions = (schemaId, statusName, userRole, companyRole, fieldPermissions, objectPermissions) =>
    _post("/api/v4/schema_permissions/update_perms/", {
        schema_id: schemaId,
        status_name: statusName,
        user_role: userRole,
        company_role: companyRole,
        field_permissions: fieldPermissions,
        object_permissions: objectPermissions,
    })

export const getBulkFormStores = (idsList, page) => {
    let url = "/api/v4/company_form_stores/id_filter/"
    // used to get the next batch of form stores
    // this endpoint only returns 20 results per request
    if (page > 1) url += `?page=${page}`
    // the last parameter is to set skipTrailingSlash to true
    // when requesting a specific page
    const trailingSlash = page > 1
    return _post(url, { ids: idsList }, 200, false, false, trailingSlash)
}

export const getCompareBulkFormStore = idsList => {
    return _post("/api/v4/company_form_stores/compare/", { ids: idsList })
}

export const createSavedFilter = data => {
    return _post("/api/v4/saved_filter_sets/", data, 201)
}

export const createTimecardTemplate = data => {
    return _post("/api/v4/timecard_templates/", data, 201)
}

export const applyTimecardTemplate = (templateId, date, mode) => {
    return _post(`/api/v4/timecard_templates/${templateId}/apply/`, { date, mode }, 200)
}

export const getReferenceableDataByResourceAndIds = (resourceName, idsList, queryParams = "") => {
    // id_filter supports a POST where the IDs to retrieve are put in the body
    return _post(`${urlForResource[resourceName]}id_filter/${queryParams}`, { ids: idsList }, 200, false, false, true)
}

export const triggerGuestPdfDownload = (formId, signedKey, uniqueRequestId) => {
    // pass along a uniqueRequestId so we only send the resulting file to the browser that requested it
    // this prevents a double download if the user has more than one window open
    return _post(`/api/v4/company_form_stores/download_as_guest/${signedKey}`, {
        form_share_id: formId,
        unique_request_id: uniqueRequestId,
        signed_key: signedKey,
    })
}

export const triggerBulkPdfDownload = (storeIds, filename, uniqueRequestId, variantName, additionalData = undefined) => {
    // pass along a uniqueRequestId so we only send the resulting file to the browser that requested it
    // this prevents a double download if the user has more than one window open
    return _post("/api/v4/company_form_stores/bulk_download_pdfs/", {
        store_ids: storeIds,
        filename,
        unique_request_id: uniqueRequestId,
        ...(variantName ? { variant: variantName } : {}),
        ...(additionalData ? additionalData : {}),
    })
}

export const requestSignature = data => {
    return _post("/api/v4/request_signature/", data, 200)
}

export const markEditsBenign = data => {
    return _post("/api/v4/mark_edits_benign/", data, 200)
}

export const createCompanySSOConfiguration = data => {
    return _post("/api/v4/company_sso_configuration/", data, 201)
}

export const refreshSSOToken = data => _post("/auth/oidc-refresh/", data, 200)

export const updateSchemaActions = payload => {
    return _post("/api/v4/schema_actions/bulk_update/", payload, 204)
}

export const updateSchemaStatuses = payload => {
    return _post("/api/v4/schema_status_names/bulk_update/", payload, 204)
}

export const addSchemaStatuses = payload => {
    return _post("/api/v4/schema_status_names/bulk_insert/", payload, 200)
}

export const addSchemaActions = payload => {
    return _post("/api/v4/schema_actions/bulk_insert/", payload, 201)
}

export const deleteSchemaActions = payload => _post("/api/v4/schema_actions/bulk_delete", payload, 204)

export const deleteSchemaStatuses = payload => _post("/api/v4/schema_status_names/bulk_delete", payload, 204)

export const updateWorkShiftProjects = (pk, payload) => _put(`/api/v4/work_shifts/${pk}/projects/`, payload, 200)

export const remapTkStatus = payload => _post("/api/v4/timekeeping_statuses/remap/", payload, 200)

export const addPicklistItem = (pk, payload) => _post(`/api/v4/picklists/${pk}/field_add_item/`, payload, 201)

export const remapPicklistItem = (id, payload) => _post(`/api/v4/picklist_items/${id}/remap/`, payload, 200)

export const getRemapPicklistItem = (id, payload) => _post(`/api/v4/picklist_items/${id}/get_remap/`, payload, 200)

export const getEmployeesByCompanySuppliedId = params => _post("/api/v4/employees/by_company_supplied_id/", params, 200)

export const sendWelcomeEmail = data => _post("/api/v4/employees/welcome_email/", data, 204)

export const sendResetPasswordEmail = data => _post("/api/v4/employees/reset_password_email/", data, 204)

export const toggleEmployeeLogin = data => _post("/api/v4/employees/toggle_login/", data, 200)

export const toggleTextAlerts = data => _post("/api/v4/employees/toggle_text_alerts/", data, 200)

export const toggleEmployeeStatus = data => _post("/api/v4/employees/toggle_status/", data, 200)

export const startExport = data => _post("/api/v4/tk_export_submissions/", data, 201)

/**
 * Duplicate one or more rate tables on the server.
 * @param {{ ids: number[] }} data - Object containing rate table IDs to duplicate.
 * @returns {Promise} Promise resolving to the array of new rate table objects.
 */
export const duplicateRateTables = data => _post("/api/v4/rate_tables/duplicate/", data, 201)

/**
 * Bulk reprice field form stores by re-running rate matching via the backend Lambda.
 * @param {{ ids: number[], fields: string[] }} data - Store IDs and list-view fields to return.
 * @returns {Promise} Promise resolving to the reprice result object.
 */
export const repriceFieldForms = data => _post("/api/v4/company_form_stores/reprice/", data, 200)

export const getPreviewData = (columnFormats, filters, options) => {
    return _post("/api/v4/tk_export_preview/", {
        column_formats: columnFormats,
        filters,
        options,
    })
}

export const validateColumnFormats = (columns, options) => {
    return _post("/api/v4/tk_export_formats/validate_column_formats/", { fields: columns, options })
}

export const assignPermissions = props => {
    const { dataType, id, employee } = props
    return _post(`/api/permissions/${dataType}/${id}/`, { employee_id: employee ?? "" })
}

export const toggleGrantsEmployeeAccess = id => {
    return _post(`/api/permissions/groups/${id}/toggle_grants_employee_access/`)
}

export const toggleGrantsProjectAccess = id => {
    return _post(`/api/permissions/groups/${id}/toggle_grants_project_access/`)
}

export const setProjectCohorts = props => {
    const { projectId, cohortIds } = props
    return _post(`/api/permissions/projects/${projectId}/set_cohorts/`, { cohort_ids: cohortIds })
}

export const setGroupCohorts = props => {
    const { groupId, cohortIds } = props
    return _post(`/api/permissions/groups/${groupId}/set_cohorts/`, { cohort_ids: cohortIds })
}

/**
 * Validate a schema without saving (preflight validation).
 *
 * @param {number} schemaId - The schema ID to validate against
 * @param {object} data - The schema data to validate (schema, ui_schema)
 * @returns {Promise<{valid: boolean, message?: string, errors?: object}>} Validation result
 */
export const validateCompanyFormSchema = (schemaId, data) => _post(`/api/v4/company_form_schemas/${schemaId}/validate_schema/`, data, 200)

/**
 * Review a schema using AI to check for best practices and potential issues.
 *
 * @param {object} data - The schema data to review
 * @param {object} data.schema - The JSON schema to review
 * @param {object} data.ui_schema - The UI schema to review
 * @param {object} [data.original_schema] - Original schema for breaking change detection
 * @param {object} [data.original_ui_schema] - Original UI schema for comparison
 * @returns {Promise<{summary: string, issues: Array, breaking_changes: Array}>} Review results
 */
export const reviewSchema = data => _post("/api/v4/schema-builder/review/", data, 200)

/**
 * Create a new CompanyFormSchema.
 *
 * @param {object} data - The schema data
 * @param {string} data.name - Name for the new schema
 * @param {object} data.schema - The JSON schema
 * @param {object} data.ui_schema - The UI schema
 * @param {string} [data.description] - Optional description
 * @returns {Promise<{id: number, name: string}>} Created schema info
 */
export const createCompanyFormSchema = data => _post("/api/v4/schema-builder/create/", data, 201)

/**
 * Generate a presigned S3 PUT URL for uploading a schema image.
 *
 * Creates a SchemaImage record and returns a presigned PUT URL for direct S3 upload.
 * The image can then be used in static-image widgets within form schemas.
 *
 * @param {object} data - The upload data
 * @param {number} data.schema_id - The schema ID to associate the image with
 * @param {string} data.content_type - MIME type of the file (e.g., "image/png")
 * @param {string} data.filename - Original filename of the image
 * @returns {Promise<object>} The created SchemaImage info with presigned upload URL:
 *     { id, absolute_url, content_type, put_url, relative_url }
 */
export const createSchemaImagePutUrl = data => _post("/api/v4/schema_images/create_put_url/", data, 200)

/**
 * Preview which projects and subgroups would be updated by a group settings cascade.
 *
 * @param {number} groupId - The source group ID
 * @param {object} payload - GroupCascadeSettingsSerializer payload
 * @returns {Promise<object>} Preflight results grouped by setting key
 */
export const preflightGroupSettingsCascade = (groupId, payload) => _post(`/api/v4/company_groups/${groupId}/preflight_cascade/`, payload, 200)

/**
 * Enqueue a cascade of selected settings from a group down to its projects and subgroups.
 *
 * @param {number} groupId - The source group ID
 * @param {object} payload - GroupCascadeSettingsSerializer payload
 * @returns {Promise<{task_id: string}>} Celery task ID to poll via getCascadeTaskStatus
 */
export const cascadeGroupSettings = (groupId, payload) => _post(`/api/v4/company_groups/${groupId}/cascade_settings/`, payload, 202)
